import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  Building2,
  Calendar,
  FileSpreadsheet,
  Home,
  Phone,
  Plus,
  Settings,
  Sparkles,
  Target,
  Users,
} from "lucide-react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useAuthStore } from "@/lib/auth-store";
import { canCreateProperty } from "@/features/settings/role-labels";
import { useCustomers } from "@/features/customer/api";
import { useProperties } from "@/features/property/api";
import { formatPrice } from "@/features/property/utils/format-price";

type CommandPaletteContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
};

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function useCommandPalette() {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error("useCommandPalette must be used within <CommandPaletteProvider>");
  }
  return ctx;
}

export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpenState] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    if (!next) {
      // Reset the query as the dialog closes so it reopens clean.
      setSearch("");
    }
  }, []);

  const toggle = useCallback(() => {
    setOpenState((v) => {
      const next = !v;
      if (!next) setSearch("");
      return next;
    });
  }, []);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggle]);

  const onNavigate = useCallback(
    (path: string) => {
      setOpen(false);
      navigate(path);
    },
    [navigate, setOpen],
  );

  const value = useMemo(() => ({ open, setOpen, toggle }), [open, setOpen, toggle]);

  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandPaletteContent
          search={search}
          onSearchChange={setSearch}
          onNavigate={onNavigate}
        />
      </CommandDialog>
    </CommandPaletteContext.Provider>
  );
}

function CommandPaletteContent({
  search,
  onSearchChange,
  onNavigate,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  onNavigate: (path: string) => void;
}) {
  const [debounced, setDebounced] = useState(search);

  // 200ms debounce — fast enough for power users, gentle on the backend.
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search.trim()), 200);
    return () => window.clearTimeout(t);
  }, [search]);

  const enabled = debounced.length >= 2;

  const propertiesQuery = useProperties(
    enabled ? { page: 0, size: 5, search: debounced } : { page: 0, size: 0 },
  );
  const customersQuery = useCustomers(
    enabled ? { page: 0, size: 5, search: debounced } : { page: 0, size: 0 },
  );

  const propertyHits = enabled ? (propertiesQuery.data?.items ?? []) : [];
  const customerHits = enabled ? (customersQuery.data?.items ?? []) : [];
  const canCreate = canCreateProperty(useAuthStore((s) => s.user?.role));

  return (
    <>
      <CommandInput
        placeholder="Tìm bất động sản, khách hàng, hoặc lệnh..."
        value={search}
        onValueChange={onSearchChange}
      />
      <CommandList>
        <CommandEmpty>
          {enabled ? "Không có kết quả." : "Gõ từ khóa để tìm kiếm."}
        </CommandEmpty>

        {propertyHits.length > 0 ? (
          <CommandGroup heading="Bất động sản">
            {propertyHits.map((property) => (
              <CommandItem
                key={`prop-${property.id}`}
                value={`property-${property.id}-${property.buildingName ?? ""}-${property.internalCode ?? ""}`}
                onSelect={() => onNavigate(`/properties/${property.id}`)}
              >
                <Building2 />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm">
                    {property.buildingName ?? "Bất động sản"}
                  </span>
                  <span className="truncate font-mono text-[11px] text-ink-faint">
                    {property.wardDisplayName} · {formatPrice(property.price, property.listingType)}
                  </span>
                </div>
                {property.internalCode ? (
                  <CommandShortcut>{property.internalCode}</CommandShortcut>
                ) : null}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {customerHits.length > 0 ? (
          <CommandGroup heading="Khách hàng">
            {customerHits.map((customer) => (
              <CommandItem
                key={`cust-${customer.id}`}
                value={`customer-${customer.id}-${customer.fullName}-${customer.phoneNumber}`}
                onSelect={() => onNavigate(`/customers/${customer.id}`)}
              >
                <Users />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm">{customer.fullName}</span>
                  <span className="inline-flex items-center gap-1 truncate font-mono text-[11px] text-ink-faint">
                    <Phone className="size-2.5" />
                    {customer.phoneNumber}
                  </span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {propertyHits.length > 0 || customerHits.length > 0 ? <CommandSeparator /> : null}

        <CommandGroup heading="Điều hướng">
          <CommandItem value="nav-dashboard" onSelect={() => onNavigate("/dashboard")}>
            <Home />
            <span>Tổng quan</span>
          </CommandItem>
          <CommandItem value="nav-properties" onSelect={() => onNavigate("/properties")}>
            <Building2 />
            <span>Bất động sản</span>
          </CommandItem>
          <CommandItem value="nav-customers" onSelect={() => onNavigate("/customers")}>
            <Users />
            <span>Khách hàng</span>
          </CommandItem>
          <CommandItem value="nav-leads" onSelect={() => onNavigate("/leads")}>
            <Target />
            <span>Khách tiềm năng</span>
          </CommandItem>
          <CommandItem value="nav-appointments" onSelect={() => onNavigate("/appointments")}>
            <Calendar />
            <span>Lịch hẹn</span>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Hành động">
          {canCreate ? (
            <>
              <CommandItem value="act-property-new" onSelect={() => onNavigate("/properties/new")}>
                <Plus />
                <span>Thêm bất động sản</span>
                <CommandShortcut>⌘N</CommandShortcut>
              </CommandItem>
              <CommandItem value="act-property-import" onSelect={() => onNavigate("/properties/import")}>
                <FileSpreadsheet />
                <span>Nhập từ Excel</span>
              </CommandItem>
            </>
          ) : null}
          <CommandItem value="act-ai-search" onSelect={() => onNavigate("/properties")}>
            <Sparkles />
            <span>Tìm BĐS bằng AI</span>
          </CommandItem>
          <CommandItem value="act-settings" onSelect={() => onNavigate("/settings")}>
            <Settings />
            <span>Cài đặt</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </>
  );
}
